import { useContext, useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Trash2, Filter, X, ChevronDown, FileText, ArrowLeft, AlertCircle } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import Pagination from '../../../../../../components/Pagination';
import auditSectionService from '../../../../../../services/audit_services/auditSectionService';
import auditPlanService from '../../../../../../services/audit_services/auditPlanService';

export default function AuditSectionsList() {
  const { auditPlanId } = useParams();
  const { hasAnyRole, authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [auditSections, setAuditSections] = useState([]);
  const [auditPlan, setAuditPlan] = useState(null);
  const [userRoleInPlan, setUserRoleInPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    ordering: 'created_at_desc'
  });

  // Separate state for applied filters (what's actually sent to API)
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    ordering: 'created_at_desc'
  });

  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [showFiltersSection, setShowFiltersSection] = useState(false);

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    auditSection: null,
    isLoading: false
  });

  // Determine if user can edit based on their role, audit plan status, and user's role in the plan
  const canEdit = hasAnyRole(['AuditUser','AuditManager']);
  
  // Only show Add/Edit Section buttons if:
  // 1. User is an auditor in that plan
  // 2. Audit plan status is 'in_progress'
  const canAddSection = (userRoleInPlan === 'auditor' && canEdit && auditPlan?.status === 'in_progress');
  const canEditSection = (userRoleInPlan === 'auditor' && canEdit && auditPlan?.status === 'in_progress');

  useEffect(() => {
    if (!auditPlanId) {
      showError('L\'ID du plan d\'audit est requis');
      return;
    }
    fetchAuditPlan();
    fetchAuditSections();
  }, [auditPlanId, pagination.page, pagination.page_size, appliedFilters]);

  const fetchAuditPlan = async () => {
    try {
      const result = await auditPlanService.getMyAuditPlanDetail(authTokens.access, auditPlanId);
      if (result.success) {
        setAuditPlan(result.data);
        // Extract user's role in this audit plan
        setUserRoleInPlan(result.data.my_role || null);
      } else {
        showError(result.error || 'Erreur lors du chargement du plan d\'audit');
      }
    } catch (error) {
      console.error('Error fetching audit plan:', error);
      showError('Erreur lors du chargement du plan d\'audit');
    }
  };

  const fetchAuditSections = async () => {
    setLoading(true);
    
    try {
      if (!auditPlanId) {
        showError('L\'ID du plan d\'audit est requis');
        setLoading(false);
        return;
      }

      const filterParams = {
        search: appliedFilters.search,
        ordering: appliedFilters.ordering
      };

      const result = await auditSectionService.getAuditSections(
        authTokens.access,
        auditPlanId,
        filterParams,
        { page: pagination.page, page_size: pagination.page_size }
      );

      if (result.success) {
        setAuditSections(result.data.results || []);
        setPagination(prev => ({
          ...prev,
          count: result.data.count || 0,
          total_pages: result.data.total_pages || 1
        }));
        
        // Set audit plan info from response if not already set
        if (result.data.audit_plan && !auditPlan) {
          setAuditPlan(result.data.audit_plan);
        }
        
        // Set user role from response
        if (result.data.my_role_in_audit_plan) {
          setUserRoleInPlan(result.data.my_role_in_audit_plan);
        }
      } else {
        showError(result.error || 'Erreur lors du chargement des sections d\'audit');
        setAuditSections([]);
      }
    } catch (error) {
      console.error('Error fetching audit sections:', error);
      showError('Erreur lors du chargement des sections d\'audit');
      setAuditSections([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    
    try {
      // TODO: Add delete method to auditSectionService
      // const result = await auditSectionService.deleteAuditSection(authTokens.access, deleteModal.auditSection.id);
      // if (result.success) {
        showSuccess(`Section "${deleteModal.auditSection.section_name}" supprimée avec succès`);
        setDeleteModal({ isOpen: false, auditSection: null, isLoading: false });
        fetchAuditSections();
      // } else {
      //   showError(result.error || 'Erreur lors de la suppression de la section d\'audit');
      //   setDeleteModal(prev => ({ ...prev, isLoading: false }));
      // }
    } catch (error) {
      console.error('Error deleting audit section:', error);
      showError('Erreur lors de la suppression de la section d\'audit');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Don't reset pagination or trigger API call here - wait for Apply button
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    const defaultFilters = {
      search: '',
      ordering: 'created_at_desc'
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des sections d'audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/audit/my-plans/${auditPlanId}`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sections d'audit</h1>
            <p className="text-sm text-gray-600 mt-1">
              {auditPlan ? `Sections pour : ${auditPlan.title}` : 'Chargement...'}
            </p>
          </div>
        </div>
        {canAddSection && (
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/create`}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter une section
          </Link>
        )}
      </div>

      {/* Status Message - Show when user is auditor but can't add/edit due to status */}
      {hasAnyRole(['AuditUser','AuditManager']) && userRoleInPlan === 'auditor' && auditPlan?.status !== 'in_progress' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-900">Modification restreinte</p>
              <p className="text-sm text-yellow-800 mt-1">
                Les sections ne peuvent être ajoutées ou modifiées que lorsque le statut du plan d'audit est "En cours". 
                Statut actuel : <span className="font-semibold">{auditPlan?.status?.replace('_', ' ').toUpperCase()}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {/* Search Bar */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom de section ou localisation..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFiltersSection(!showFiltersSection)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFiltersSection ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Filters Section */}
        {showFiltersSection && (
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 gap-4">
              {/* Sorting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Trier par</label>
                <select
                  value={filters.ordering}
                  onChange={(e) => handleFilterChange('ordering', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="created_at_desc">Plus récent d'abord</option>
                  <option value="created_at_asc">Plus ancien d'abord</option>
                  <option value="section_name_asc">Nom (A-Z)</option>
                  <option value="section_name_desc">Nom (Z-A)</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex items-center justify-end space-x-3 mt-4">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="h-4 w-4 mr-2" />
                Effacer les filtres
              </button>
              <button
                onClick={applyFilters}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Filter className="h-4 w-4 mr-2" />
                Appliquer les filtres
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sections Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {auditSections.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucune section d'audit trouvée</p>
            {canAddSection && (
              <Link
                to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/create`}
                className="inline-flex items-center mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer votre première section
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Section
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Localisation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Questions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mis à jour
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditSections.map((section) => {
                  return (
                    <tr key={section.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-600 text-xs font-semibold rounded-full">
                            {section.order || 1}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{section.section_name}</p>
                            <p className="text-xs text-gray-500">Créé par {section.created_by_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600">{section.location}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">{section.questions_count || 0}</div>
                          <div className="text-xs text-gray-500">Total</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(section.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${section.id}`}
                            className="text-green-600 hover:text-green-900"
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          {canEditSection && (
                            <>
                              <Link
                                to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${section.id}/edit`}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Modifier"
                              >
                                <Edit className="h-4 w-4" />
                              </Link>
                              {/* <button
                                onClick={() => setDeleteModal({ isOpen: true, auditSection: section, isLoading: false })}
                                className="text-red-600 hover:text-red-900"
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button> */}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {auditSections.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          pageSize={pagination.page_size}
          onPageSizeChange={(size) => setPagination(prev => ({ ...prev, page_size: size, page: 1 }))}
          totalItems={pagination.count}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, auditSection: null, isLoading: false })}
        onConfirm={handleDelete}
        title="Supprimer la section d'audit"
        message={`Êtes-vous sûr de vouloir supprimer "${deleteModal.auditSection?.section_name}" ? Cela supprimera également toutes les questions associées. Cette action ne peut pas être annulée.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isLoading={deleteModal.isLoading}
        type="danger"
      />
    </div>
  );
}